import { BackendModule, Namespace, ResourceKey, TOptions, i18n as i18nInterface } from 'i18next';
import { useTranslation as i18nextUseTranslation } from 'react-i18next';

export let i18nInstance: i18nInterface;

export type { TOptions };

/**
 * i18next library creates new instance on import.
 * Call this function from the package where you initialize i18next
 * so all code utilizing this file, will use the same i18n instance.
 */
export const bindI18n = (i18n: i18nInterface) => {
  i18nInstance = i18n;
};

/** What vite-plugin-po-to-json exports for a `.po` file. Use as the generic of `import.meta.glob`. */
export type CatalogueModule = { default: ResourceKey };

type CatalogueGlob = Record<string, () => Promise<CatalogueModule>>;

/**
 * i18next backend that serves the `.po` catalogues picked up by `import.meta.glob`.
 * Vite's po-to-json plugin turns each file into an i18next JSON module on the fly,
 * so instead of fetching anything we just import the matching module.
 *
 * Unknown namespaces resolve to an empty catalogue on purpose: a typo in
 * `useTranslation('...')` shows up as untranslated UI instead of silently
 * being served some other namespace's strings.
 *
 * @param catalogues one glob map per namespace, keyed by the namespace name
 */
export const createGlobBackend = (catalogues: Record<string, CatalogueGlob>): BackendModule => ({
  type: 'backend',
  init: () => {},
  read: async (lng, ns, callback) => {
    const glob = catalogues[ns] ?? {};
    const file = Object.keys(glob).find((path) => path.endsWith(`/${lng}.po`));

    if (!file) {
      callback(null, {});
      return;
    }

    const mod = await glob[file]!();

    callback(null, mod.default);
  },
});

/**
 * React hook for utilizing i18n.
 */
export const useTranslation = (ns?: Namespace) => {
  return i18nextUseTranslation(ns, {
    i18n: i18nInstance,
  });
};

/**
 * Translate plural string
 *
 * @param singular
 * @param plural
 * @param options Array of following options:
 *  - count - number for pluralization
 *  - ...
 * @param context context string which is only needed for 'gettext-extractor' package to parse the context correctly
 */
export const translatePlural = function (singular: string, plural: string, options: TOptions = {}, context = ''): string {
  const { language, t, services } = i18nInstance;
  options.defaultValue = singular;

  let res = t(singular, options);

  if (options.count === 1) { // early exit for singular
    return res;
  }

  // `t` above falls back to the English SINGULAR when the active language has no translation
  // for this string yet - interpolate the English plural default instead ("2 items", not "2 item").
  // English itself ships no catalog, so it always takes this path.
  // The existence check gets the same options as `t`: the catalog holds a plural string only under
  // its `_one` / `_other` keys, which i18next looks up from the count - without it the bare key is
  // never found and every translated plural came back in English.
  if (language.startsWith('en') || !i18nInstance.exists(singular, options)) {
    res = services.interpolator.interpolate(
      plural,
      { ...options },
      language,
      options.interpolation || {}
    );
  }

  return res;
};
